import { type QueryKey, useQuery } from '@tanstack/react-query';
import { memo, type ReactElement, useCallback, useMemo, useState } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { projectsGridConfig, useProjectFilter } from '@/features/projects';
import { queryProjectSummaries } from '@/features/projects/data';
import { TableSegmentBreakdownCell } from '@/features/shared/components/tableSystem/TableSegmentBreakdownCell';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { queryKeys } from '@/shared/hooks';
import { uiTokens } from '@/theme/uiTokens';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { TableTextCell } from '@/ui/composites/table';
import { type GridColDef, type GridRenderCellParams } from '@/ui/primitives';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { ProjectDto, ProjectSummaryDto, QueryResult } from '@flowqa/shared';

type ProjectsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  noRowsText?: string;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<ProjectDto>>;
  queryKey?: QueryKey;
  summariesQueryFn?: (projectIds: string[]) => Promise<ProjectSummaryDto[]>;
  summariesQueryKey?: QueryKey;
  onRowsChange?: (rows: ProjectDto[]) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  isAuthenticated?: boolean;
};

type ProjectSummaryRow = ProjectDto & {
  releaseSummary?: null;
  featureSummary?: null;
  suiteSummary?: null;
  testSummary?: null;
};

type StatusSegment = { label: string; value: number; color: string };

const columnFlex = { name: 1, releases: 1, features: 1, suites: 1, tests: 1 };

const ProjectsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  noRowsText = 'No projects found',
  queryFn,
  queryKey,
  summariesQueryFn,
  summariesQueryKey,
  onRowsChange,
  onDataUpdatedAt,
  showLastUpdated,
  isAuthenticated = true,
}: ProjectsTableProps): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const [visibleProjectIds, setVisibleProjectIds] = useState<string[]>([]);
  const projectIds = useMemo(
    () => Array.from(new Set(visibleProjectIds)).sort(),
    [visibleProjectIds],
  );
  const projectIdsKey = useMemo(() => JSON.stringify(projectIds), [projectIds]);
  const canQueryRelated = projectIds.length > 0;
  const summariesQuery = useQuery({
    queryKey:
      summariesQueryKey ??
      queryKeys.projectSummaries(selectedOrganisationId ?? null, projectIdsKey),
    enabled: canQueryRelated,
    queryFn: async () =>
      summariesQueryFn
        ? await summariesQueryFn(projectIds)
        : await queryProjectSummaries(projectIds, selectedOrganisationId ?? undefined),
  });
  const summariesByProjectId = useMemo(() => {
    const map = new Map<string, ProjectSummaryDto>();
    const items = summariesQuery.data ?? [];
    items.forEach((summary) => {
      map.set(summary.projectId, summary);
    });
    return map;
  }, [summariesQuery.data]);
  const columns: Array<GridColDef<ProjectSummaryRow>> = useMemo(() => {
    const stringOperators = getStringFilterOperators();
    const nameColumn: GridColDef<ProjectSummaryRow> = {
      field: 'name',
      headerName: 'Project',
      flex: columnFlex.name,
      cellClassName: 'table-name-cell',
      filterOperators: stringOperators,
      renderCell: (params) => (
        <TableTextCell
          primary={{
            label: params.row.name,
            href: `/projects/${params.row.slug}`,
            onClick: () => {
              setSelectedProjectId(params.row.id);
            },
          }}
        />
      ),
    };
    const releasesColumn: GridColDef<ProjectSummaryRow> = {
      field: 'releaseSummary',
      headerName: 'Releases',
      flex: columnFlex.releases,
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'table-breakdown-cell',
      filterable: false,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<ProjectSummaryRow, ProjectSummaryRow['releaseSummary']>,
      ) => {
        const summary = summariesByProjectId.get(params.row.id)?.releaseSummary ?? {
          total: 0,
          draft: 0,
          planned: 0,
          overdue: 0,
          ready: 0,
          released: 0,
        };
        const segments: StatusSegment[] = [
          { label: 'Draft', value: summary.draft, color: uiTokens.statusColors.neutral },
          { label: 'Planned', value: summary.planned, color: uiTokens.statusColors.blue },
          { label: 'Ready', value: summary.ready, color: uiTokens.statusColors.orange },
          { label: 'Released', value: summary.released, color: uiTokens.statusColors.green },
          { label: 'Overdue', value: summary.overdue, color: uiTokens.statusColors.red },
        ];
        return (
          <TableSegmentBreakdownCell
            totalLabel={`${summary.total} release${summary.total === 1 ? '' : 's'}`}
            rows={segments.map((segment) => ({
              key: segment.label,
              label: `${segment.value.toLocaleString()} ${segment.label.toLowerCase()}`,
              count: segment.value,
              color: segment.color,
            }))}
          />
        );
      },
    };
    const featuresColumn: GridColDef<ProjectSummaryRow> = {
      field: 'featureSummary',
      headerName: 'Features',
      flex: columnFlex.features,
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'table-breakdown-cell',
      filterable: false,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<ProjectSummaryRow, ProjectSummaryRow['featureSummary']>,
      ) => {
        const summary = summariesByProjectId.get(params.row.id)?.featureSummary ?? {
          total: 0,
          draft: 0,
          planned: 0,
          ready: 0,
          released: 0,
        };
        const segments: StatusSegment[] = [
          { label: 'Draft', value: summary.draft, color: uiTokens.statusColors.neutral },
          { label: 'Planned', value: summary.planned, color: uiTokens.statusColors.blue },
          { label: 'Ready', value: summary.ready, color: uiTokens.statusColors.orange },
          { label: 'Released', value: summary.released, color: uiTokens.statusColors.green },
        ];
        return (
          <TableSegmentBreakdownCell
            totalLabel={`${summary.total} feature${summary.total === 1 ? '' : 's'}`}
            rows={segments.map((segment) => ({
              key: segment.label,
              label: `${segment.value.toLocaleString()} ${segment.label.toLowerCase()}`,
              count: segment.value,
              color: segment.color,
            }))}
          />
        );
      },
    };
    const suitesColumn: GridColDef<ProjectSummaryRow> = {
      field: 'suiteSummary',
      headerName: 'Latest Suites',
      flex: columnFlex.suites,
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'table-breakdown-cell',
      filterable: false,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<ProjectSummaryRow, ProjectSummaryRow['suiteSummary']>,
      ) => {
        const summary = summariesByProjectId.get(params.row.id);
        const executionCounts = {
          passed: summary?.suiteLatestRunPassed,
          failed: summary?.suiteLatestRunFailed,
          errored: summary?.suiteLatestRunErrored,
          skipped: summary?.suiteLatestRunSkipped,
          untested: summary?.suiteLatestRunUntested,
        };
        return (
          <TableStatusBreakdownCell
            entity="suite"
            counts={{
              passed: executionCounts.passed ?? 0,
              failed: executionCounts.failed ?? 0,
              errored: executionCounts.errored ?? 0,
              skipped: executionCounts.skipped ?? 0,
              untested: executionCounts.untested ?? 0,
            }}
          />
        );
      },
    };
    const testsColumn: GridColDef<ProjectSummaryRow> = {
      field: 'testSummary',
      headerName: 'Latest Tests',
      flex: columnFlex.tests,
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'table-breakdown-cell',
      filterable: false,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<ProjectSummaryRow, ProjectSummaryRow['testSummary']>,
      ) => {
        const summary = summariesByProjectId.get(params.row.id);
        const executionCounts = {
          passed: summary?.testLatestRunPassed,
          failed: summary?.testLatestRunFailed,
          errored: summary?.testLatestRunErrored,
          skipped: summary?.testLatestRunSkipped,
          untested: summary?.testLatestRunUntested,
        };
        return (
          <TableStatusBreakdownCell
            entity="test"
            counts={{
              passed: executionCounts.passed ?? 0,
              failed: executionCounts.failed ?? 0,
              errored: executionCounts.errored ?? 0,
              skipped: executionCounts.skipped ?? 0,
              untested: executionCounts.untested ?? 0,
            }}
          />
        );
      },
    };
    return [nameColumn, releasesColumn, featuresColumn, suitesColumn, testsColumn];
  }, [setSelectedProjectId, summariesByProjectId]);

  const handleRowsChange = useCallback(
    (rows: ProjectDto[]) => {
      setVisibleProjectIds(rows.map((row) => row.id));
      onRowsChange?.(rows);
    },
    [onRowsChange],
  );

  const gridConfig = projectsGridConfig({
    orgId: selectedOrganisationId,
    persistKey: persistKey ?? 'projects-table',
    syncStateToUrl,
  });

  return (
    <ServerDataTable<ProjectSummaryRow>
      queryPath="projects/query"
      {...gridConfig}
      queryFn={queryFn ?? gridConfig.queryFn}
      queryKey={queryKey ?? gridConfig.queryKey}
      columns={columns}
      getRowId={(row) => row.id}
      isAuthenticated={isAuthenticated}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      pageSizeOptions={pageSizeOptions}
      initialPageSize={initialPageSize}
      disablePageSizeSelect={disablePageSizeSelect}
      noRowsText={noRowsText}
      interactionMode="static"
      skeletonGetRowHeight={null}
      showLastUpdated={showLastUpdated}
      autoHeight
      onRowsChange={handleRowsChange}
      onDataUpdatedAt={onDataUpdatedAt}
    />
  );
};

export const ProjectsTable = memo(ProjectsTableComponent);
